import React, { useState } from 'react';
import type { LocalizedString } from '../../types';
import type { AdminOption, OptionInput } from '../../services/adminApi';
import LocalizedInput from './LocalizedInput';
import PriceInput from './PriceInput';
import { price_to_fields, fields_to_price } from './price';
import type { PriceFields } from './price';
import { EMPTY_LOCALIZED, lines_to_array, array_to_lines } from './localized';
import styles from './AdminCrud.module.css';

interface OptionFormProps {
  initial?: AdminOption;
  onSubmit: (input: OptionInput) => Promise<void>;
  onCancel: () => void;
}

/** Create/edit form for an option with localized fields and multi-currency price */
const OptionForm: React.FC<OptionFormProps> = ({ initial, onSubmit, onCancel }) => {
  const [title, setTitle] = useState<LocalizedString>(initial?.title ?? EMPTY_LOCALIZED);
  const [description, setDescription] = useState<LocalizedString>(
    initial?.description ?? EMPTY_LOCALIZED
  );
  const [price, setPrice] = useState<PriceFields>(price_to_fields(initial?.price));
  const [pros, setPros] = useState<LocalizedString>(array_to_lines(initial?.pros));
  const [cons, setCons] = useState<LocalizedString>(array_to_lines(initial?.cons));
  const [days, setDays] = useState<string>(
    initial?.days_to_complete !== undefined ? String(initial.days_to_complete) : ''
  );
  const [isPerHour, setIsPerHour] = useState<boolean>(initial?.is_per_hour ?? false);
  const [allowMultiple, setAllowMultiple] = useState<boolean>(
    initial?.allow_multiple_instances ?? false
  );
  const [minInstances, setMinInstances] = useState<string>(
    initial?.min_instances !== undefined ? String(initial.min_instances) : ''
  );
  const [maxInstances, setMaxInstances] = useState<string>(
    initial?.max_instances !== undefined ? String(initial.max_instances) : ''
  );
  const [imageUrl, setImageUrl] = useState<string>(initial?.image?.url ?? '');
  const [imageTitle, setImageTitle] = useState<LocalizedString>(
    initial?.image?.title ?? EMPTY_LOCALIZED
  );
  const [sortOrder, setSortOrder] = useState<string>(String(initial?.sort_order ?? 0));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handle_submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!title.ru.trim()) {
      setError('Укажите название на русском');
      return;
    }

    if (price.us.trim() === '' && (price.eu.trim() !== '' || price.kzt.trim() !== '')) {
      setError('Укажите цену в долларах — EUR/KZT считаются от неё');
      return;
    }

    setIsSaving(true);
    try {
      await onSubmit({
        title,
        description,
        price: fields_to_price(price),
        pros: lines_to_array(pros),
        cons: lines_to_array(cons),
        days_to_complete: days.trim() === '' ? null : Number(days),
        is_per_hour: isPerHour,
        allow_multiple_instances: allowMultiple,
        min_instances:
          allowMultiple && minInstances.trim() !== '' ? Number(minInstances) : null,
        max_instances:
          allowMultiple && maxInstances.trim() !== '' ? Number(maxInstances) : null,
        image: imageUrl.trim() ? { url: imageUrl.trim(), title: imageTitle } : null,
        sort_order: Number(sortOrder) || 0,
      });
    } catch (err) {
      setError((err as Error).message || 'Failed to save option');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form className={styles.form_panel} onSubmit={handle_submit}>
      <LocalizedInput
        label="Option title"
        field_key="title"
        value={title}
        onChange={setTitle}
      />
      <div style={{ marginTop: 'var(--spacing-md)' }}>
        <LocalizedInput
          label="Description"
          field_key="description"
          value={description}
          onChange={setDescription}
          textarea
        />
      </div>

      <div style={{ marginTop: 'var(--spacing-md)' }}>
        <PriceInput value={price} onChange={setPrice} />
      </div>

      <div className={styles.form_grid} style={{ marginTop: 'var(--spacing-md)' }}>
        <div className={styles.field}>
          <span className={styles.field_label}>Days to complete</span>
          <input
            className={styles.input}
            type="number"
            min={0}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.field_label}>Sort order</span>
          <input
            className={styles.input}
            type="number"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          />
        </div>
      </div>

      <div style={{ marginTop: 'var(--spacing-md)' }}>
        <LocalizedInput
          label="Pros (one per line)"
          field_key="pros"
          kind="array"
          value={pros}
          onChange={setPros}
          textarea
        />
      </div>
      <div style={{ marginTop: 'var(--spacing-md)' }}>
        <LocalizedInput
          label="Cons (one per line)"
          field_key="cons"
          kind="array"
          value={cons}
          onChange={setCons}
          textarea
        />
      </div>

      <div className={styles.form_grid} style={{ marginTop: 'var(--spacing-md)' }}>
        <label className={styles.checkbox_row}>
          <input
            type="checkbox"
            checked={isPerHour}
            onChange={(event) => setIsPerHour(event.target.checked)}
          />
          Priced per hour
        </label>
        <label className={styles.checkbox_row}>
          <input
            type="checkbox"
            checked={allowMultiple}
            onChange={(event) => setAllowMultiple(event.target.checked)}
          />
          Allow multiple instances
        </label>
        {allowMultiple && (
          <>
            <div className={styles.field}>
              <span className={styles.field_label}>Min instances</span>
              <input
                className={styles.input}
                type="number"
                min={0}
                value={minInstances}
                onChange={(event) => setMinInstances(event.target.value)}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.field_label}>Max instances</span>
              <input
                className={styles.input}
                type="number"
                min={1}
                value={maxInstances}
                onChange={(event) => setMaxInstances(event.target.value)}
              />
            </div>
          </>
        )}
      </div>

      <div className={styles.field} style={{ marginTop: 'var(--spacing-md)' }}>
        <span className={styles.field_label}>Image URL (optional)</span>
        <input
          className={styles.input}
          value={imageUrl}
          onChange={(event) => setImageUrl(event.target.value)}
        />
      </div>
      {imageUrl.trim() && (
        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <LocalizedInput
            label="Image title"
            field_key="image.title"
            value={imageTitle}
            onChange={setImageTitle}
          />
        </div>
      )}

      {error && <p className={styles.error_text}>{error}</p>}

      <div className={styles.actions_row}>
        <button type="submit" className={styles.button_primary} disabled={isSaving}>
          {isSaving ? 'Saving…' : initial ? 'Save option' : 'Add option'}
        </button>
        <button type="button" className={styles.button_secondary} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

export default OptionForm;
